import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";

interface RouterValue {
  path: string;
  hash: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

const readLocation = (): { path: string; hash: string } => ({
  path: window.location.pathname || "/",
  hash: window.location.hash.replace(/^#/, ""),
});

/** Scrolls to an in-page anchor, or to the top when there is none. */
function applyScroll(hash: string): void {
  if (!hash) {
    window.scrollTo({ top: 0, behavior: "auto" });
    return;
  }
  const target = document.getElementById(hash);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(readLocation);

  useEffect(() => {
    const onPop = () => setLocation(readLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const url = new URL(to, window.location.origin);
    const next = { path: url.pathname || "/", hash: url.hash.replace(/^#/, "") };
    const samePage = next.path === window.location.pathname;

    if (options?.replace) {
      window.history.replaceState({}, "", url.pathname + url.hash);
    } else {
      window.history.pushState({}, "", url.pathname + url.hash);
    }
    setLocation(next);

    // Same-page anchors need the element to exist already; cross-page jumps
    // wait a frame so the new route can mount first.
    if (samePage) {
      applyScroll(next.hash);
    } else {
      window.requestAnimationFrame(() => applyScroll(next.hash));
    }
  }, []);

  const value = useMemo<RouterValue>(
    () => ({ path: location.path, hash: location.hash, navigate }),
    [location.path, location.hash, navigate],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error("useRouter must be used inside RouterProvider");
  return value;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };

/** Internal link. Leaves modified clicks and new-tab clicks to the browser. */
export function Link({ to, onClick, ...rest }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        if (rest.target && rest.target !== "_self") return;
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    />
  );
}
